import styles from './messages.module.css'
import { useState, useEffect, useMemo } from 'react';

import { useScreenSize, ScreenSize } from '#/hooks/useScreenSize';
import { ChecklistState, useChecklist } from '#/contexts/ChecklistContext';

import { useBranding } from '#/branding/BrandingContext';
import { UserMessageType, AiMessageType, DisplayItem, Image } from '#/types/message.ts';
import { ContentModal } from '#/components/content-modal/content-modal.tsx';
import ContentCarousel from './content-carousel/content-carousel.tsx';
import { PromptSuggestions } from './prompt-suggestions/prompt-suggestions.tsx';
import UserMessage from './user-message/user-message.tsx';
import AiMessage from './ai-message/ai-message.tsx';
import Loader from './loader/loader.tsx';
import { fetchImageUrls } from '#/api/images';
import logger from "#/utils/logger";

interface MessagesProps {
  userMessages: UserMessageType[];
  aiMessages: AiMessageType[];
  onFeatureSelect: (selections: { feature_name: string, option_name: string }[]) => void;
  isAnalyzingMetadata: boolean;
  audioOutputFileName?: string;
  isMuted: boolean;
  onSubmit: (prompt: string) => void;
  isExhibitionMode: boolean;
  welcomeAudioUrl?: string;
}

interface AiMessageCarouselContentProps {
  aiMessage: AiMessageType;
  imageListId: string;
  openModal: (item: DisplayItem, index: number, allItemsFromCarousel: DisplayItem[], imageListId: string) => void;
  analyzingMetadata: boolean;
}

const AiMessageCarouselContent: React.FC<AiMessageCarouselContentProps> = ({
  aiMessage,
  imageListId,
  openModal,
  analyzingMetadata,
}) => {
  const combinedItems = useMemo(() => {
    return aiMessage.metadata?.display_items || null;
  }, [aiMessage.metadata]);

  return (
    <ContentCarousel
      items={combinedItems}
      onItemClick={(item: DisplayItem, itemIndex: number, allItemsFromCarousel: DisplayItem[]) => {
        openModal(item, itemIndex, allItemsFromCarousel, imageListId);
      }}
      analyzingMetadata={analyzingMetadata}
    />
  );
};

export default function Messages({ userMessages, aiMessages, onFeatureSelect,
                                   isAnalyzingMetadata, audioOutputFileName,
                                   isMuted, onSubmit, isExhibitionMode, welcomeAudioUrl }: MessagesProps) {
  const screenSize = useScreenSize();
  const { checklistState } = useChecklist();
  const { avatar } = useBranding();

  const isWaitingForResponse = userMessages.length > 0 &&
    (aiMessages.length === 0 || userMessages[userMessages.length - 1].timestamp > aiMessages[aiMessages.length - 1].timestamp);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalItem, setModalItem] = useState<DisplayItem | null>(null);
  const [modalItemList, setModalItemList] = useState<DisplayItem[]>([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const [heroImageSrc, setHeroImageSrc] = useState<string | null>(null);

  const openModal = (
    item: DisplayItem,
    index: number,
    allItemsFromCarousel: DisplayItem[],
  ) => {
    setModalItem(item);
    setModalItemList(allItemsFromCarousel);
    setActiveImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalItem(null);
    setModalItemList([]);
  };

  const [checklistVisible, setChecklistVisible] = useState(false);  // TODO: Remove this. Should be in the checklist context

  useEffect(() => {
    setChecklistVisible(checklistState === ChecklistState.OPEN);
  }, [checklistState]);

  const latestAiMessageIndex = aiMessages.length - 1;
  const firstAiMessage = aiMessages[0];
  const displayItems = firstAiMessage?.metadata?.display_items ?? [];
  const imageItems = displayItems.filter(
    (item): item is Image => item.item_type === 'image'
  );
  const firstImage = imageItems[0] ?? null;
  const firstImageFileName = firstImage?.file_name;

  useEffect(() => {
    if (!firstImageFileName) return;
    let cancelled = false;
    fetchImageUrls([firstImageFileName])
      .then(urlMap => {
        if (cancelled) return;
        setHeroImageSrc(urlMap[firstImageFileName] || firstImageFileName);
      })
      .catch((e: any) => {
        if (cancelled) return;
        logger.error("Image url fetch failed: " + e.message);
        setHeroImageSrc(firstImageFileName);
      });
    return () => { cancelled = true; };
  }, [firstImageFileName]);

  return (
    <div className={`${styles.messages} ${checklistVisible ? styles.shiftContent : ''}`}>

      {/* Render initial AI message (index 0) if it exists */}
      {aiMessages.length > 0 && (
        <div className={styles.messageContainer} key={`ai-0-container`}>
          {firstImage && (
            <div className={styles.heroImageContainer}>
              <img
                src={heroImageSrc ?? undefined}   // ✅ use fetched URL if available
                className={styles.heroImage}
              />
            </div>
          )}

          <div className={styles.aiMessageRow}>
            <div className={styles.aiMessageContentRow}>
            {screenSize === ScreenSize.DESKTOP && (
              <div className={styles.aiIconContainer}>
                <div className={styles.aiIconCircle}>
                  <img src={avatar} className={styles.spogenEye} alt="ai" />
                </div>
              </div>
            )}
              <div className={styles.aiMessageWrapper}>
                <AiMessage
                  message={aiMessages[0]}
                  openModal={openModal}
                  onFeatureSelect={onFeatureSelect}
                  isLatestMessage={0 === latestAiMessageIndex}
                  audioOutputFileName={audioOutputFileName}
                  isMuted={isMuted}
                  isFirstMessage={true}
                  isExhibitionMode={isExhibitionMode}
                  overrideAudioUrl={welcomeAudioUrl}
                />
              </div>
            </div>

            {0 === latestAiMessageIndex &&
              Array.isArray(aiMessages[0].metadata?.prompt_suggestions) &&
              aiMessages[0].metadata.prompt_suggestions.length > 0 && (
                <PromptSuggestions
                  suggestions={aiMessages[0].metadata.prompt_suggestions}
                  onSuggestionClick={onSubmit}
                />
            )}
          </div>
        </div>
      )}

      {/* Map through user messages to render subsequent pairs */}
      {userMessages.map((userMsg, index) => {
        const aiMessageForThisUserMessage = aiMessages[index + 1]; // AI message corresponding to this user message
        const aiMessageIndex = index + 1;
        const imageListId = `ai-${index + 1}`;

        // This AI message is the target for analysis if global isAnalyzingMetadata is true
        // AND this is the last AI message in the list.
        const isThisMessageTheCurrentTargetForAnalysis =
          isAnalyzingMetadata && (index + 1 === aiMessages.length - 1);

        const isWaitingForThisAiMessage = !aiMessageForThisUserMessage && index === userMessages.length - 1;

        return (
          <div key={userMsg.timestamp} className={styles.messageContainer}>
            <UserMessage message={userMsg} isExhibitionMode={isExhibitionMode}/>

            {/* Render AiMessageCarouselContent if aiMessageForThisUserMessage object exists.
                It will handle potentially missing metadata internally. */}
            {aiMessageForThisUserMessage ? (
              <>
                <AiMessageCarouselContent
                  aiMessage={aiMessageForThisUserMessage}
                  imageListId={imageListId}
                  openModal={openModal}
                  analyzingMetadata={isThisMessageTheCurrentTargetForAnalysis}
                />

                <div className={styles.aiMessageRow}>
                  <div className={styles.aiMessageContentRow}>
                    {screenSize === ScreenSize.DESKTOP && (!isMuted || !isWaitingForThisAiMessage) && (
                      <div className={styles.aiIconContainer}>
                        <div className={styles.aiIconCircle}>
                          <img src={avatar} className={styles.spogenEye} alt="ai" />
                        </div>
                      </div>
                    )}
                    <div className={styles.aiMessageWrapper}>
                      <AiMessage
                        message={aiMessageForThisUserMessage}
                        openModal={openModal}
                        onFeatureSelect={onFeatureSelect}
                        isLatestMessage={aiMessageIndex === latestAiMessageIndex}
                        audioOutputFileName={audioOutputFileName}
                        isMuted={isMuted}
                        isFirstMessage={false}
                        isExhibitionMode={isExhibitionMode}
                      />
                    </div>
                  </div>

                  {aiMessageIndex === latestAiMessageIndex &&
                    Array.isArray(aiMessageForThisUserMessage.metadata?.prompt_suggestions) &&
                    aiMessageForThisUserMessage.metadata.prompt_suggestions.length > 0 && aiMessageForThisUserMessage.metadata.context_relevant && (
                      <PromptSuggestions
                        suggestions={aiMessageForThisUserMessage.metadata.prompt_suggestions}
                        onSuggestionClick={onSubmit}
                      />
                  )}
                </div>
              </>
            ) : (
              isWaitingForThisAiMessage && <Loader isLoading={true} />
            )}
          </div>
        );
      })}

      {/* Fallback loader: if the very first AI message is awaited and no user messages yet */}
      {isWaitingForResponse && (
        <div className={styles.messageContainer}>
          <div className={styles.loaderContainer}>
            <Loader isLoading={true} />
          </div>
        </div>
      )}

      <ContentModal
        isOpen={isModalOpen}
        currentItem={modalItem}
        items={modalItemList}
        activeIndex={activeImageIndex}
        onClose={closeModal}
        onNext={() => {
          if (modalItemList && activeImageIndex < modalItemList.length - 1) {
            const nextIndex = activeImageIndex + 1;
            setActiveImageIndex(nextIndex);
            setModalItem(modalItemList[nextIndex]);
          }
        }}
        onPrevious={() => {
          if (modalItemList && activeImageIndex > 0) {
            const prevIndex = activeImageIndex - 1;
            setActiveImageIndex(prevIndex);
            setModalItem(modalItemList[prevIndex]);
          }
        }}
      />
    </div>
  );
}
